import React, { useState, createContext, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import TranditionalRaffleWinnerDialogComponent from "./tranditionalRaffleWinnerDialog.component";
import { hideBackdrop, showBackdrop } from "modules/backdrop";
import { addAlert } from "modules/notification";
import { PRIZE_TYPE_PRESET } from "modules/lucky-draw-v2/constants";
import luckyDrawV2Api from "app/api/luckyDrawV2";
import { getApiLang, getLang } from "app/feature/constants";
import {AccessRights } from "lib/constants/accessRights";
import { canAccess } from "../../../../profile/redux/selector/profile.selector";

export const PrizeContext = createContext();

export default function TranditionalRaffleWinnerDialogContainer({
  isOpen,
  handleClose,
  luckyDrawUuid,
  index,
  selectedLuckyDraw,
}) {
  const dispatch = useDispatch();
  const hasLuckyDrawV2ViewAccessRight = useSelector((state) =>
    canAccess(state, AccessRights.CampaignV2.LUCKY_DRAW_LIST_READ));
  const accessToken = useSelector((state) => state.session.accessToken);
  const [prizeList, setPrizeList] = useState([]);
  // Store the last saved state for cancel/reset
  const [originalPrizeList, setOriginalPrizeList] = useState([]);
  const [originalExcludeList, setOriginalExcludeList] = useState([]);
  const [viewType] = useState(true);
  const [isEdit, setIsEdit] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [excludeList, setExcludeList] = useState([]);
  const [eligibleParticipantsData, setEligibleParticipantsData] =
    useState(null);
  const winnerType = useState(PRIZE_TYPE_PRESET);
  const [isUpdating, setIsUpdating] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: null, // 'all' or 'prize'
    prizeIndex: null,
    prizeName: "",
    count: 0,
  });

  const progress = useSelector(
    (state) => state.luckyDraw.LuckyDrawDetail.progress
  );
  const lang = useSelector((state) => state.constant.languages);

  useEffect(() => {
    if (!isOpen) {
      setIsEdit(false);
    }
  }, [isOpen]);

  const handleSaveWinner = async () => {
    dispatch(showBackdrop());

    let hasEmptyField = false;

    const prizesUpdateInfo = [];
    for (const prize of prizeList) {
      const prizeWinners = {
        id: prize.id,
        winnerParticipantIds: [],
      };
      let count = 0;
      for (const winner of prize.winners) {
        count++;

        if (winner.participantId) {
          prizeWinners.winnerParticipantIds.push(winner.participantId);
        }
      }

      if (prizeWinners.winnerParticipantIds < count) {
        hasEmptyField = true;
        break;
      }

      prizesUpdateInfo.push(prizeWinners);
    }

    if (hasEmptyField) {
      dispatch(hideBackdrop());
      dispatch(
        addAlert({
          severity: "error",
          message: getLang(lang, "message.error.FAILED_SET_WINNER"),
        })
      );
      return;
    }

    dispatch(hideBackdrop());
  };

  const handleConfirmWinner = async () => {
    const prizesUpdateInfo = [];
    for (const prize of prizeList) {
      const prizeWinners = {
        prizeId: prize.id,
        enc: [],
      };

      for (const winner of prize.winners) {
        if (!winner.status && winner.enc) prizeWinners.enc.push(winner.enc);
      }
      prizesUpdateInfo.push(prizeWinners);
    }
    dispatch(showBackdrop());
    await luckyDrawV2Api
      .presetWinner({
        luckyDrawUuid: luckyDrawUuid,
        winnerField: prizesUpdateInfo,
      })
      .then((response) => {
        // Update the original states to reflect the new saved state
        setOriginalPrizeList(JSON.parse(JSON.stringify(prizeList)));
        setOriginalExcludeList(JSON.parse(JSON.stringify(excludeList)));
        setIsEdit(false);
        handleClose();
      })
      .catch((error) => {
        dispatch(
          addAlert({ severity: "error", message: getApiLang(lang, error.code) })
        );
      })
      .finally(() => {
        dispatch(hideBackdrop());
      });
  };

  const handleSetWinner = async (
    index,
    winnerIndex,
    serialNumber,
    enc,
    participantData = null
  ) => {
    // Use the passed participant data if available, otherwise fall back to search logic
    let participant = null;

    if (participantData) {
      // Use the complete participant data from the dropdown
      participant = {
        id: participantData.id,
        name: participantData.name,
        email: participantData.email,
        contact_no: participantData.contact_no,
      };
    } else {
      // Fall back to searching in eligibleParticipantsData (for randomization)
      if (eligibleParticipantsData && eligibleParticipantsData.prizes) {
        for (const p of eligibleParticipantsData.prizes) {
          const found = p.eligible_participants.find(
            (ep) => ep.serial_number === serialNumber && ep.enc === enc
          );
          if (found) {
            participant = {
              id: found.id,
              name: found.name,
              email: found.email,
              contact_no:
                found.contact_no ||
                found.contactNumber ||
                found.phone ||
                found.phone_no,
            };
            break;
          }
        }
      }

      // If contact_no is still not available, try to get it from the getPresetTRParticipants API
      if (participant && !participant.contact_no) {
        try {
          const response = await luckyDrawV2Api.getPresetTRParticipants({
            uuid: luckyDrawUuid,
            search: serialNumber,
            limit: 1,
            skip: 0,
          });
          const apiParticipantData = response.data.data?.data?.find(
            (p) => p.serial_number === serialNumber && p.enc === enc
          );
          if (apiParticipantData) {
            participant.contact_no =
              apiParticipantData.contact_no ||
              apiParticipantData.contactNumber ||
              apiParticipantData.phone ||
              apiParticipantData.phone_no;
          }
        } catch (error) {
          // Error fetching participant details - continue without contact_no
        }
      }
    }
    const info = {
      index: index,
      winnerIndex: winnerIndex,
      serial_number: serialNumber,
      enc: enc,
      participant: participant,
    };
    const updatedPrizeList = [...prizeList];
    const prize = updatedPrizeList[index];
    const updatedWinners = [...prize.winners];

    updatedWinners[winnerIndex] = { ...updatedWinners[winnerIndex], ...info };
    updatedPrizeList[index] = { ...prize, winners: updatedWinners };

    let encs = updatedPrizeList.reduce((acc, prize) => {
      let winner = prize.winners;
      winner.forEach((element) => {
        if (element.enc) {
          acc.push({ enc: element.enc });
        }
      });

      return acc;
    }, []);

    setPrizeList(updatedPrizeList);
    setExcludeList(encs);
  };

  const handleDelete = (index, winnerIndex, serialNumber, enc) => {
    const updatedPrizeList = [...prizeList];
    const prize = updatedPrizeList[index];
    const updatedWinners = [...prize.winners];

    // Clear the winner data instead of removing the slot
    updatedWinners[winnerIndex] = {
      enc: null,
      serial_number: null,
      participant: null,
    };

    updatedPrizeList[index] = { ...prize, winners: updatedWinners };

    let encs = updatedPrizeList.reduce((acc, prize) => {
      let winner = prize.winners;
      winner.forEach((element) => {
        if (element.enc) {
          acc.push({ enc: element.enc });
        }
      });

      return acc;
    }, []);

    setPrizeList(updatedPrizeList);
    setExcludeList(encs);
  };

  const fetchPrizes = useCallback(async () => {
    setIsFetching(true);
    try {
      // Fetch both prizes and eligible participants
      const [prizesResponse, eligibleResponse] = await Promise.all([
        luckyDrawV2Api.getPrizes({ luckyDrawUuid: luckyDrawUuid }),
        luckyDrawV2Api.getEligibleParticipants({
          luckyDrawUuid: luckyDrawUuid,
        }),
      ]);

      // Handle prizes data
      const prizesData = prizesResponse.data;
      if (prizesData) {
        prizesData.prizes.forEach((prize) => {
          const remainingWinners = prize.quantity - prize.winners.length;
          if (remainingWinners <= 0) {
            return prize;
          }

          for (let i = 0; i < remainingWinners; i++) {
            prize.winners.push({
              enc: null,
              serial_number: null,
              participant: null,
            });
          }
            return prize;
        });
        setPrizeList(prizesData.prizes);
        setOriginalPrizeList(JSON.parse(JSON.stringify(prizesData.prizes)));
        setExcludeList(prizesData.exclude_list);
        setOriginalExcludeList(
          JSON.parse(JSON.stringify(prizesData.exclude_list))
        );
      } else {
        setPrizeList([]);
      }

      // Handle eligible participants data
      const eligibleData = eligibleResponse.data.data;
      setEligibleParticipantsData(eligibleData);
    } catch (error) {
      dispatch(
        addAlert({ severity: "error", message: getApiLang(lang, error.code) })
      );
    } finally {
      setIsFetching(false);
    }
  }, [luckyDrawUuid, dispatch, lang]);

  const [item, setItem] = useState([]);

  const toggleItem = (index) => {
    setItem((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const expandAll = () => {
    setItem(prizeList.map((_, index) => index));
  };

  const collapseAll = () => {
    setItem([]);
  };

  // Helper function to get total available (empty) winner slots count across all prizes - DYNAMIC
  const getTotalAvailableWinnersCount = () => {
    if (!prizeList || prizeList.length === 0) return 0;
    return prizeList.reduce((total, prize) => {
      // Count empty slots in this prize
      const emptySlots = prize.winners.filter(
        (winner) => !winner.participant || winner.participant === null
      ).length;
      return total + emptySlots;
    }, 0);
  };

  // Helper function to get available (empty) winner slots count for a specific prize - DYNAMIC
  const getAvailableWinnersCountForPrize = (prizeIndex) => {
    if (!prizeList || !prizeList[prizeIndex]) return 0;
    const prize = prizeList[prizeIndex];
    // Count empty slots in this specific prize
    const emptySlots = prize.winners.filter(
      (winner) => !winner.participant || winner.participant === null
    ).length;
    return emptySlots;
  };

  const handleShowConfirmDialog = (type, prizeIndex = null) => {
    if (!eligibleParticipantsData) {
      dispatch(
        addAlert({
          severity: "error",
          message: "Eligible participants data not loaded",
        })
      );
      return;
    }

    if (type === "all") {
      const totalCount = getTotalAvailableWinnersCount();
      setConfirmDialog({
        open: true,
        type: "all",
        prizeIndex: null,
        prizeName: "",
        count: totalCount,
      });
    } else if (type === "prize" && prizeIndex !== null) {
      const prize = prizeList[prizeIndex];
      const count = getAvailableWinnersCountForPrize(prizeIndex);
      setConfirmDialog({
        open: true,
        type: "prize",
        prizeIndex: prizeIndex,
        prizeName: prize.name,
        count: count,
      });
    }
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      type: null,
      prizeIndex: null,
      prizeName: "",
      count: 0,
    });
  };

  const handleConfirmRandomize = () => {
    if (confirmDialog.type === "all") {
      handleRandomizeAllPrizesExecute();
    } else if (confirmDialog.type === "prize") {
      handleRandomizeForPrizeExecute(confirmDialog.prizeIndex);
    }
    handleCloseConfirmDialog();
  };

  const handleRandomizeForPrize = (prizeIndex) => {
    handleShowConfirmDialog("prize", prizeIndex);
  };

  const handleRandomizeForPrizeExecute = async (prizeIndex) => {
    // Set to edit mode if not already
    if (!isEdit) {
      setIsEdit(true);
    }

    const updatedPrizeList = [...prizeList];
    const prize = updatedPrizeList[prizeIndex];

    // Get currently used USER IDs from LOCAL state only (not server state)
    const usedUserIds = new Set();
    const usedSerialNumbers = new Set();
    updatedPrizeList.forEach((p) => {
      p.winners.forEach((winner) => {
        if (winner.participant && winner.participant.user_id) {
          usedUserIds.add(winner.participant.user_id);
        }
        if (winner.serial_number) {
          usedSerialNumbers.add(winner.serial_number);
        }
      });
    });

    // Find empty winner slots in current prize that need to be filled
    const updatedWinners = [...prize.winners];
    const emptySlots = [];
    updatedWinners.forEach((winner, index) => {
      if (!winner.participant || winner.participant === null) {
        emptySlots.push(index);
      }
    });

    if (emptySlots.length === 0) {
      dispatch(
        addAlert({
          severity: "info",
          message: "All winner slots are already filled for this prize",
        })
      );
      return;
    }

    // Get UNIQUE participants from API (already filtered to exclude existing winners)
    try {
      const response = await luckyDrawV2Api.getPresetTRParticipantsUnique({
        uuid: luckyDrawUuid,
        search: "",
        limit: 1000,
        skip: 0,
      });

      const uniqueParticipants = response.data.data?.data || [];

      // Additional filter based on LOCAL state only - exclude currently used participants (by USER ID and serial number)
      const availableParticipants = uniqueParticipants.filter(
        (p) =>
          !usedUserIds.has(p.user_id) && !usedSerialNumbers.has(p.serial_number)
      );

      if (availableParticipants.length === 0) {
        dispatch(
          addAlert({
            severity: "warning",
            message: "No available participants for randomization",
          })
        );
        return;
      }

      // Use the unique participants directly (they already have complete data including contact_no)
      const completeAvailableParticipants = availableParticipants;

      // Randomize and select winners only for empty slots
      const shuffled = [...completeAvailableParticipants].sort(
        () => Math.random() - 0.5
      );
      const winnersNeeded = Math.min(emptySlots.length, shuffled.length);
      const selectedWinners = shuffled.slice(0, winnersNeeded);

      // Fill only the empty slots with new winners
      selectedWinners.forEach((winner, index) => {
        const slotIndex = emptySlots[index];
        updatedWinners[slotIndex] = {
          enc: winner.enc,
          serial_number: winner.serial_number,
          participant: {
            id: winner.id,
            user_id: winner.user_id, // Store user_id for tracking duplicates
            name: winner.name,
            email: winner.email,
            contact_no: winner.contact_no,
          },
          index: prizeIndex,
          winnerIndex: slotIndex,
        };
      });

      updatedPrizeList[prizeIndex] = { ...prize, winners: updatedWinners };

      // Update exclude list
      let encs = updatedPrizeList.reduce((acc, prize) => {
        let winner = prize.winners;
        winner.forEach((element) => {
          if (element.enc) {
            acc.push({ enc: element.enc });
          }
        });
        return acc;
      }, []);

      setPrizeList(updatedPrizeList);
      setExcludeList(encs);

      dispatch(
        addAlert({
          severity: "success",
          message: `Winners randomized for ${prize.name}!`,
        })
      );
    } catch (error) {
      dispatch(
        addAlert({
          severity: "error",
          message: "Failed to randomize winners. Please try again.",
        })
      );
    }
  };

  const handleRandomizeIndividualWinner = async (prizeIndex, winnerIndex) => {
    // Set to edit mode if not already
    if (!isEdit) {
      setIsEdit(true);
    }

    const updatedPrizeList = [...prizeList];
    const prize = updatedPrizeList[prizeIndex];

    // Check if the current slot already has a winner
    if (prize.winners[winnerIndex] && prize.winners[winnerIndex].participant) {
      dispatch(
        addAlert({
          severity: "info",
          message: `Winner slot #${
            winnerIndex + 1
          } already has a selected winner. Delete the current winner first if you want to randomize a new one.`,
        })
      );
      return;
    }

    try {
      // Get UNIQUE participants from API (already filtered to exclude existing winners)
      const response = await luckyDrawV2Api.getPresetTRParticipantsUnique({
        uuid: luckyDrawUuid,
        search: "",
        limit: 1000,
        skip: 0,
      });

      const uniqueParticipants = response.data.data?.data || [];

      // Get currently used USER IDs and serial numbers from LOCAL state only
      const usedUserIds = new Set();
      const usedSerialNumbers = new Set();
      updatedPrizeList.forEach((p) => {
        p.winners.forEach((winner) => {
          if (winner.participant && winner.participant.user_id) {
            usedUserIds.add(winner.participant.user_id);
          }
          if (winner.serial_number) {
            usedSerialNumbers.add(winner.serial_number);
          }
        });
      });

      // Additional filter based on LOCAL state only (by USER ID and serial number)
      const availableParticipants = uniqueParticipants.filter(
        (p) =>
          !usedUserIds.has(p.user_id) && !usedSerialNumbers.has(p.serial_number)
      );

      if (availableParticipants.length === 0) {
        dispatch(
          addAlert({
            severity: "warning",
            message: "No available participants for randomization",
          })
        );
        return;
      }

      // Use unique participants directly (they already have complete data including contact_no)
      const completeAvailableParticipants = availableParticipants;

      // Randomly select one winner
      const randomIndex = Math.floor(
        Math.random() * completeAvailableParticipants.length
      );
      const selectedWinner = completeAvailableParticipants[randomIndex];

      // Update the specific winner slot
      const updatedWinners = [...prize.winners];
      updatedWinners[winnerIndex] = {
        enc: selectedWinner.enc,
        serial_number: selectedWinner.serial_number,
        participant: {
          id: selectedWinner.id,
          user_id: selectedWinner.user_id, // Store user_id for tracking duplicates
          name: selectedWinner.name,
          email: selectedWinner.email,
          contact_no: selectedWinner.contact_no,
        },
        index: prizeIndex,
        winnerIndex: winnerIndex,
      };

      updatedPrizeList[prizeIndex] = { ...prize, winners: updatedWinners };

      // Update exclude list
      let encs = updatedPrizeList.reduce((acc, prize) => {
        let winner = prize.winners;
        winner.forEach((element) => {
          if (element.enc) {
            acc.push({ enc: element.enc });
          }
        });
        return acc;
      }, []);

      setPrizeList(updatedPrizeList);
      setExcludeList(encs);

      dispatch(
        addAlert({
          severity: "success",
          message: `Winner #${winnerIndex + 1} randomized for ${prize.name}!`,
        })
      );
    } catch (error) {
      dispatch(
        addAlert({
          severity: "error",
          message: "Failed to randomize winner. Please try again.",
        })
      );
    }
  };

  const handleRandomizeAllPrizes = () => {
    handleShowConfirmDialog("all");
  };

  const handleRandomizeAllPrizesExecute = async () => {
    try {
      dispatch(showBackdrop());

      // Set to edit mode if not already
      if (!isEdit) {
        setIsEdit(true);
      }

      // Get UNIQUE participants from API (already filtered to exclude existing winners)
      const response = await luckyDrawV2Api.getPresetTRParticipantsUnique({
        uuid: luckyDrawUuid,
        search: "",
        limit: 1000,
        skip: 0,
      });

      const uniqueParticipants = response.data.data?.data || [];
      const usedUserIds = new Set();
      const updatedPrizeList = [...prizeList];

      // First, collect all existing winners to mark them as used
      const usedSerialNumbers = new Set();
      updatedPrizeList.forEach((prize) => {
        prize.winners.forEach((winner) => {
          if (winner.participant && winner.participant.user_id) {
            usedUserIds.add(winner.participant.user_id);
          }
          if (winner.serial_number) {
            usedSerialNumbers.add(winner.serial_number);
          }
        });
      });

      // Randomize winners for each prize - only fill empty slots
      for (let i = 0; i < updatedPrizeList.length; i++) {
        const prize = updatedPrizeList[i];

        // Find empty winner slots in current prize
        const updatedWinners = [...prize.winners];
        const emptySlots = [];
        updatedWinners.forEach((winner, index) => {
          if (!winner.participant || winner.participant === null) {
            emptySlots.push(index);
          }
        });

        // Skip if no empty slots
        if (emptySlots.length === 0) continue;

        // Additional filter out already used participants from UNIQUE participants (by USER ID and serial number)
        const availableParticipants = uniqueParticipants.filter(
          (p) =>
            !usedUserIds.has(p.user_id) &&
            !usedSerialNumbers.has(p.serial_number)
        );

        // Shuffle and select random winners only for empty slots
        const shuffled = [...availableParticipants].sort(
          () => Math.random() - 0.5
        );
        const winnersNeeded = Math.min(emptySlots.length, shuffled.length);
        const selectedWinners = shuffled.slice(0, winnersNeeded);

        // Mark selected participants as used (by both USER ID and serial number)
        selectedWinners.forEach((winner) => {
          usedUserIds.add(winner.user_id);
          usedSerialNumbers.add(winner.serial_number);
        });

        // Fill only the empty slots with new winners
        selectedWinners.forEach((winner, index) => {
          const slotIndex = emptySlots[index];
          updatedWinners[slotIndex] = {
            enc: winner.enc,
            serial_number: winner.serial_number,
            participant: {
              id: winner.id,
              user_id: winner.user_id, // Store user_id for tracking duplicates
              name: winner.name,
              email: winner.email,
              contact_no: winner.contact_no,
            },
            index: i,
            winnerIndex: slotIndex,
          };
        });

        // Note: We only fill empty slots, don't clear any existing winners

        const prizeListIndex = updatedPrizeList.findIndex(
          (p) => p.id === prize.id
        );
        if (prizeListIndex !== -1) {
          updatedPrizeList[prizeListIndex] = {
            ...prize,
            winners: updatedWinners,
          };
        }
      }

      // Update exclude list
      let encs = updatedPrizeList.reduce((acc, prize) => {
        let winner = prize.winners;
        winner.forEach((element) => {
          if (element.enc) {
            acc.push({ enc: element.enc });
          }
        });
        return acc;
      }, []);

      setPrizeList(updatedPrizeList);
      setExcludeList(encs);

      dispatch(
        addAlert({
          severity: "success",
          message: getLang(lang, "message.success.RANDOMIZE_COMPLETED"),
        })
      );
    } catch (error) {
      dispatch(
        addAlert({
          severity: "error",
          message:
            getApiLang(lang, error.code) || "Failed to randomize winners",
        })
      );
    } finally {
      dispatch(hideBackdrop());
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchPrizes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, luckyDrawUuid]);

  const handleDownloadWinner = (id) => {
    setIsExporting(true);
    luckyDrawV2Api
      .exportWinnerList({ id: id, token: accessToken })
      .finally(() => {
        setIsExporting(false);
        return;
      });
  };

  const handleUpdateClaimFulfillment = (status, uuid) => {
    setIsUpdating(true);
    luckyDrawV2Api
      .updateWinnerClaimFulfillmentStatus({
        luckyDrawUuid: selectedLuckyDraw.uuid,
        winnerUuid: uuid,
        status: status,
      })
      .then(() => {
        fetchPrizes();
      })
      .catch((error) => {
        dispatch(
          addAlert({
            severity: "error",
            message: getApiLang(lang, error.code),
          })
        );
      })
      .finally(() => {
        setIsUpdating(false);
      });
  };

  return (
    <>
      <PrizeContext.Provider
        value={{
          luckyDrawUuid,
          prizeList,
          excludeList,
          viewType,
          isEdit,
          isUpdating,
          handleSetWinner,
          handleDelete,
          handleUpdateClaimFulfillment,
          handleRandomizeForPrize,
          handleRandomizeIndividualWinner,
          eligibleParticipantsData,
          getAvailableWinnersCountForPrize,
          luckyDraw: selectedLuckyDraw,
        }}
      >
        <TranditionalRaffleWinnerDialogComponent
          isOpen={isOpen}
          handleClose={handleClose}
          handleSaveWinner={handleSaveWinner}
          winnerType={winnerType}
          changeToUpdate={() => setIsEdit(true)}
          changeToView={() => {
            setPrizeList(JSON.parse(JSON.stringify(originalPrizeList)));
            setExcludeList(JSON.parse(JSON.stringify(originalExcludeList)));
            setIsEdit(false);
          }}
          handleConfirmWinner={handleConfirmWinner}
          progress={progress}
          lang={lang}
          isFetching={isFetching}
          selectedLuckyDraw={selectedLuckyDraw}
          hasLuckyDrawV2ViewAccessRight={hasLuckyDrawV2ViewAccessRight}
          item={item}
          toggleItem={toggleItem}
          expandAll={expandAll}
          collapseAll={collapseAll}
          handleDownloadWinner={handleDownloadWinner}
          isExporting={isExporting}
          handleRandomizeAllPrizes={handleRandomizeAllPrizes}
          getTotalAvailableWinnersCount={getTotalAvailableWinnersCount}
          confirmDialog={confirmDialog}
          handleCloseConfirmDialog={handleCloseConfirmDialog}
          handleConfirmRandomize={handleConfirmRandomize}
          isEdit={isEdit}
        />
      </PrizeContext.Provider>
    </>
  );
}
